import {
  BookOpen,
  Clapperboard,
  Disc3,
  Gamepad2,
  LucideIcon,
  Tv,
} from "lucide-react";

import { CreditRole } from "../types/Credit";
import { Status } from "../types/Tracking";
import { WorkType } from "../types/Work";

export const WORK_TYPES: WorkType[] = ["book", "movie", "tv", "game", "music"];

export const WORK_LABELS: Record<WorkType, string> = {
  book: "Book",
  movie: "Movie",
  tv: "TV",
  game: "Game",
  // An album, EP or single (ADR 0052), each shown by its kind (`kindOf`).
  music: "Music",
};

/**
 * The types someone's shelves hold anything of, in `WORK_TYPES` order: the
 * types a profile shows. Their stats count only the types they keep (ADR
 * 0044), so a type they've turned off is always empty and never shows.
 * With nothing shelved at all, every type.
 */
export const shelvedTypes = (
  library: Partial<Record<WorkType, Record<string, number>>>,
): WorkType[] => {
  const kept = WORK_TYPES.filter((type) =>
    Object.values(library[type] ?? {}).some((n) => n > 0),
  );
  return kept.length ? kept : WORK_TYPES;
};

// What a count of each type is called: "346 books".
export const WORK_PLURALS: Record<WorkType, string> = {
  book: "Books",
  movie: "Movies",
  tv: "TV",
  game: "Games",
  music: "Music",
};

// What a goal of each type counts (ADR 0062): "52 books", "20 seasons", a
// show's seasons since a show still airing never finishes whole, and
// albums, never singles.
const GOAL_UNITS: Record<WorkType, [string, string]> = {
  book: ["book", "books"],
  movie: ["movie", "movies"],
  tv: ["season", "seasons"],
  game: ["game", "games"],
  music: ["album", "albums"],
};

export const goalUnit = (type: WorkType, n: number): string =>
  `${n} ${GOAL_UNITS[type][n === 1 ? 0 : 1]}`;

// What a goal's count is labelled, beside it or over its field.
export const GOAL_LABELS: Record<WorkType, string> = {
  book: "Books",
  movie: "Movies",
  tv: "TV seasons",
  game: "Games",
  music: "Albums",
};

// Types named in a sentence: "movies and TV".
export const typesNamed = (types: WorkType[]): string =>
  types
    .map((t) => (t === "tv" ? "TV" : WORK_PLURALS[t].toLowerCase()))
    .join(" and ");

// Lucide icons per media type; render them through <WorkIcon />.
export const WORK_ICONS: Record<WorkType, LucideIcon> = {
  book: BookOpen,
  movie: Clapperboard,
  tv: Tv,
  game: Gamepad2,
  music: Disc3,
};

export const STATUSES: Status[] = [
  "want",
  "in_progress",
  "completed",
  "dropped",
];

export const STATUS_LABELS: Record<Status, string> = {
  want: "Want",
  in_progress: "In progress",
  completed: "Completed",
  dropped: "Dropped",
};

// The verb used for "in progress" varies by media type.
export const progressVerb = (type: WorkType): string =>
  ({
    book: "Reading",
    movie: "Watching",
    tv: "Watching",
    game: "Playing",
    music: "Listening",
  })[type];

// Another time through something finished: "Rereading" / "Rewatching" /
// "Replaying" (an entry's `againSince`).
export const againVerb = (type: WorkType): string =>
  ({
    book: "Rereading",
    movie: "Rewatching",
    tv: "Rewatching",
    game: "Replaying",
    music: "Relistening",
  })[type];

// What starts one: "Reread" / "Rewatch" / "Replay".
export const againAction = (type: WorkType): string =>
  ({
    book: "Reread",
    movie: "Rewatch",
    tv: "Rewatch",
    game: "Replay",
    music: "Relisten",
  })[type];

// Past-tense verb for a finished engagement: "Read" / "Watched" / "Played" /
// "Listened".
export const pastVerb = (type: WorkType): string =>
  ({
    book: "Read",
    movie: "Watched",
    tv: "Watched",
    game: "Played",
    music: "Listened",
  })[type];

// Bare verb for actions/labels: "read" / "watch" / "play" / "listen".
export const logActionVerb = (type: WorkType): string =>
  ({
    book: "read",
    movie: "watch",
    tv: "watch",
    game: "play",
    music: "listen",
  })[type];

export const IMPORT_SOURCES = [
  "backloggd",
  "calibre",
  "epilog",
  "fable",
  "goodreads",
  "grouvee",
  "hardcover",
  "howlongtobeat",
  "imdb",
  "letterboxd",
  "myanimelist",
  "serializd",
  "simkl",
  "steam",
  "storygraph",
  "trakt",
  "tvtime",
] as const;

// Each import source's name as the app itself writes it.
export const importSourceLabel = (source: string): string =>
  (
    ({
      backloggd: "Backloggd",
      calibre: "Calibre",
      epilog: "Epilog",
      fable: "Fable",
      goodreads: "Goodreads",
      grouvee: "Grouvee",
      hardcover: "Hardcover",
      howlongtobeat: "HowLongToBeat",
      imdb: "IMDb",
      letterboxd: "Letterboxd",
      myanimelist: "MyAnimeList",
      serializd: "Serializd",
      simkl: "Simkl",
      steam: "Steam",
      storygraph: "StoryGraph",
      trakt: "Trakt",
      tvtime: "TV Time",
    }) as Record<string, string>
  )[source] ?? source.charAt(0).toUpperCase() + source.slice(1);

// Credit roles in display order, with their section labels.
export const CREDIT_ROLE_LABELS: [CreditRole, string][] = [
  ["author", "Written by"],
  ["illustrator", "Illustrated by"],
  ["introduction", "Introduction by"],
  ["director", "Directed by"],
  ["showrunner", "Created by"],
  ["developer", "Developed by"],
  ["artist", "By"],
  ["writer", "Screenplay"],
  ["actor", "Cast"],
  ["voice_actor", "Voice cast"],
  ["composer", "Music"],
  ["producer", "Produced by"],
  ["publisher", "Published by"],
  ["production_company", "Production"],
  ["network", "Network"],
];

// Short role names for a contributor's credit list ("Director, Writer").
export const CREDIT_ROLE_NAMES: Record<CreditRole, string> = {
  author: "Author",
  director: "Director",
  actor: "Actor",
  voice_actor: "Voice actor",
  writer: "Writer",
  composer: "Composer",
  developer: "Developer",
  publisher: "Publisher",
  production_company: "Production",
  network: "Network",
  showrunner: "Creator",
  artist: "Artist",
  producer: "Producer",
  illustrator: "Illustrator",
  introduction: "Introduction",
};

// The headline credit per type: who a work is "by". The API returns these as
// `creators`; the credits list leaves them out.
export const PRIMARY_ROLE: Record<WorkType, CreditRole> = {
  book: "author",
  movie: "director",
  tv: "showrunner",
  game: "developer",
  music: "artist",
};
